import { PROPERTY_NOT_FOUND } from 'utils/constants';
import { readFromObj } from 'utils/utils';
import { getOriginalValue, getSourceValue } from '../formControlSlice';

export const resolveConditionalValue = (value, lookup) => {
    if (!lookup?.dataSource || typeof value !== 'string') return value;
    const matched = lookup.dataSource.find(
        (option) => option[lookup.displayExpr] === value || option[lookup.displayExpr]?.toLowerCase?.() === value.toLowerCase()
    );
    if (matched) return matched[lookup.valueExpr];
    return value;
};

// Empty means "no value the agent can see": undefined, null, or empty string. NOTE: `0` and `false`
// are intentionally treated as NON-empty. That is correct for the current text/lookup fields
// (sealNumber, FourKites, OCR); if a boolean field ever gets a legitimate `false` autofill, revisit.
export const isFormFieldValueEmpty = (value) => value === undefined || value === null || value === '';

// Single source of truth for "does the field currently hold the value we autofilled". Uses a loose,
// string-coerced compare so numeric vs string values (e.g. sealNumber '12345' vs 12345) still match.
// Both the pill visibility check and the value-change effect MUST use this so a pill cannot be shown
// by one rule and immediately dropped by a stricter one (which caused pill flicker on numeric fields).
export const matchesAutofillValue = (currentValue, autofillValue) => currentValue === autofillValue || String(currentValue) === String(autofillValue);

// Product rule for conditional autofill coming from the validate endpoint (BEAST-11753):
//  - only apply when the backend actually returned a value, AND
//  - the field is empty: either never touched, OR the agent deliberately cleared it (empty input).
// If the agent left ANY content in the field (even one character), keep their value and skip autofill.
export const shouldApplyConditionalAutofill = ({ autofillValue, userEdited, currentValue }) => {
    if (isFormFieldValueEmpty(autofillValue)) return false;
    if (userEdited && !isFormFieldValueEmpty(currentValue)) return false;
    return true;
};

// Decides whether the "Autofilled" pill should be shown next to a field. It must stay stable across
// the extra render pass the hook does AFTER the autofill has been written: at that point the field is
// no longer empty, so `shouldApplyConditionalAutofill` alone would flip to false and the pill would
// vanish. We therefore also show the pill when the field already holds the resolved autofill value
// (i.e. the autofill actually landed). If the agent kept their own different content, no pill.
export const shouldShowConditionalAutofillPill = ({ autofillValue, userEdited, currentValue, lookup }) => {
    if (isFormFieldValueEmpty(autofillValue)) return false;
    if (shouldApplyConditionalAutofill({ autofillValue, userEdited, currentValue })) return true;
    return matchesAutofillValue(currentValue, resolveConditionalValue(autofillValue, lookup));
};

export const getRawFieldValue = (source, fieldName) => {
    let value = readFromObj(source, fieldName);

    if ((value === undefined || value === PROPERTY_NOT_FOUND) && fieldName.includes('.')) {
        const flatKey = fieldName.split('.').pop();
        value = readFromObj(source, flatKey);
    }
    if (value === PROPERTY_NOT_FOUND) return null;
    return value;
};

export const getFieldValueWithFallback = (source, fieldName, lookup, useLookupOriginal = false) => {
    let value = useLookupOriginal ? getOriginalValue(source, fieldName, lookup) : getSourceValue(source, fieldName, lookup);
    if (!value && fieldName.includes('.')) {
        const flatKey = fieldName.split('.').pop();
        value = useLookupOriginal ? getOriginalValue(source, flatKey, lookup) : getSourceValue(source, flatKey, lookup);
    }
    return value;
};

const ANNOUNCEMENT_CELL_PREFIX = 'announcement.';

const isAssignableFormField = (field) => !!field && field.editable && field.dataField !== 'sendToBison' && typeof field.dataField === 'string';

const findFormFieldsForCell = (dataField, formFields, cellToFormFieldMap) => {
    const matches = [];
    const direct = formFields.find((f) => f.dataField === dataField);
    if (isAssignableFormField(direct)) matches.push(direct);

    if (dataField.startsWith(ANNOUNCEMENT_CELL_PREFIX)) {
        const lastSegment = dataField.slice(ANNOUNCEMENT_CELL_PREFIX.length);
        if (lastSegment && !lastSegment.includes('.')) {
            for (const field of formFields) {
                if (!isAssignableFormField(field) || matches.includes(field)) continue;
                const parts = field.dataField.split('.');
                if (parts.length > 1 && parts[parts.length - 1] === lastSegment) matches.push(field);
            }
        }
    }

    if (cellToFormFieldMap) {
        const lookupKeys = [];
        if (dataField) lookupKeys.push(dataField);
        if (dataField.startsWith(ANNOUNCEMENT_CELL_PREFIX)) {
            const stripped = dataField.slice(ANNOUNCEMENT_CELL_PREFIX.length);
            if (stripped) lookupKeys.push(stripped);
        }
        for (const lookupKey of lookupKeys) {
            const targetFormDataField = cellToFormFieldMap[lookupKey];
            if (!targetFormDataField) continue;
            const mapped = formFields.find((f) => f.dataField === targetFormDataField);
            if (isAssignableFormField(mapped) && !matches.includes(mapped)) matches.push(mapped);
        }
    }

    return matches;
};

export const buildPartialFieldMappings = (cells, formFields, cellToFormFieldMap) => {
    const map = {};
    if (!Array.isArray(cells) || !Array.isArray(formFields)) return map;
    for (const cell of cells) {
        const dataField = cell?.dataField;
        if (!dataField || dataField === 'sendToBison') continue;
        const matched = findFormFieldsForCell(dataField, formFields, cellToFormFieldMap);
        for (const fieldCfg of matched) {
            if (!map[fieldCfg.dataField]) map[fieldCfg.dataField] = { fieldCfg, cell };
        }
    }
    return map;
};

const resolveAutomationValues = ({ cell, fieldCfg, rowData }) => {
    const { lookup } = fieldCfg;

    if (cell && cell.value != null && cell.value !== '') {
        const rawValue = cell.value;
        const lookupMatch = lookup?.dataSource?.find?.((option) => option[lookup.valueExpr ?? lookup.displayExpr] === rawValue);
        const sourceValue = lookupMatch?.[lookup?.displayExpr] ?? cell.displayValue ?? rawValue;
        return { sourceValue, originalValue: rawValue };
    }

    if (!rowData) return { sourceValue: null, originalValue: null };
    return {
        sourceValue: getSourceValue(rowData, fieldCfg.dataField, lookup),
        originalValue: getOriginalValue(rowData, fieldCfg.dataField, lookup),
    };
};

export const buildPartialAutomation = ({ fieldMappings, rowData, source }) => {
    const automation = {};
    if (!fieldMappings) return automation;

    for (const fieldName of Object.keys(fieldMappings)) {
        const { fieldCfg, cell } = fieldMappings[fieldName];
        if (!fieldCfg) continue;

        const { sourceValue, originalValue } = resolveAutomationValues({ cell, fieldCfg, rowData });
        if (!sourceValue || typeof sourceValue === 'object') continue;

        automation[fieldName] = {
            sourceValue,
            ...(originalValue != null && { originalValue }),
            source,
        };
    }

    return automation;
};
